import { useEffect, useMemo, useState } from 'react';
import { carrierApi } from '../../../services/api';
import { logger } from '../../../utils/logger';
import type { Violation } from '../../../types/api';

export interface ViolationsSummary {
  total: number;
  bySeverity: { critical: number; high: number; medium: number; low: number };
  oosCount: number;
}

export interface ViolationsBySeverity {
  CRITICAL: Violation[];
  HIGH: Violation[];
  MEDIUM: Violation[];
  LOW: Violation[];
}

/**
 * Fetches the violations summary from the backend (accurate totals across
 * the full carrier history) and memo-groups the loaded violations by
 * severity so the Overview/Violations tabs can render counts and lists.
 */
export function useViolationsSummary(dotNumber: number, violations: Violation[]) {
  const [violationsSummary, setViolationsSummary] = useState<ViolationsSummary | null>(null);

  useEffect(() => {
    if (dotNumber) {
      carrierApi.getViolationsSummary(dotNumber)
        .then(summary => {
          setViolationsSummary(summary);
          logger.debug('Loaded violations summary', { dotNumber, summary });
        })
        .catch(err => {
          logger.warn('Failed to load violations summary', { dotNumber, error: err });
        });
    }
  }, [dotNumber]);

  const violationsBySeverity = useMemo<ViolationsBySeverity>(() => ({
    CRITICAL: violations.filter(v => v.severity === 'CRITICAL'),
    HIGH: violations.filter(v => v.severity === 'HIGH'),
    MEDIUM: violations.filter(v => v.severity === 'MEDIUM'),
    LOW: violations.filter(v => v.severity === 'LOW'),
  }), [violations]);

  return { violationsSummary, violationsBySeverity };
}
